import RegisterForm from '@pages/register/components/RegisterForm';
import { useFormik } from 'formik';
import { Loading } from '@components/atoms/Loading';
import useRegister from '@hooks/api/auth/useRegister';
import { Header } from '@pages/login/components/Header';
import { registerSchema } from './schemaValidations';
import { useMemo, useState } from 'react';
import AccountExists, {
  AccountExistsReason,
} from '@pages/register/components/AccountExists';
import Alert from '@components/atoms/Alert';
import { PaymentMethodType } from '@models/subscription';
import { track } from '@config/analytics';
import { useTranslation } from '@hooks/useTranslation';

// Coded reason strings only — sign_up_failed must never carry raw error text.
const KNOWN_REASON_CODES = new Set([
  'ALREADY_INVITED',
  'EMAIL_ALREADY_REGISTERED',
  'NO_INVITATION_FOUND',
]);

/**
 * Sign-up. No email verification: the flow is deliberately the shortest it can be.
 *
 * The chosen plan drives the rest. A card-free plan hits `POST /auth/register` and starts
 * a trial that asks for payment when it expires; a plan with `requiresPaymentUpfront`
 * collects a card first and hits `POST /auth/register-with-payment`. There is no default
 * plan — the user always picks one, which is what makes the two configurable.
 *
 * Joining an existing organization goes through the invitation path, which is separately
 * verified by construction — the invite email is what proves the address.
 */
const RegisterPage: React.FC = () => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — see docs/architecture.md "## Sprint 28" Decision 4.
  const tLogin = useTranslation('login');
  const registerValidationSchema = useMemo(() => registerSchema(tLogin), [tLogin]);
  const { register, isLoading } = useRegister();
  const [showAlreadyInvited, setShowAlreadyInvited] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Which flavour of "this address is taken" to explain. They need different guidance:
  // an invited user is waiting on an email, an existing account holder just needs to
  // sign in and create the organization from their Profile page.
  const [existingAccountReason, setExistingAccountReason] =
    useState<AccountExistsReason>('invited');

  const registerFormik = useFormik({
    validationSchema: registerValidationSchema,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      organizationType: '', // 'create' or 'join' - User must choose
      organizationName: '',
      // Deliberately empty: sign-up never preselects a plan.
      planId: '',
      // Mirror of planId consumed by PaymentMethodSelector's pricing.
      selectedPlanId: '',
      // Mirror of the selected plan's display name — GA4's `plan` param needs a name, never an id.
      selectedPlanName: '',
      requiresCard: false,
      paymentMethodType: '' as PaymentMethodType | '',
      billingPeriod: 'MONTHLY',
      billingFirstName: '',
      billingLastName: '',
      billingEmail: '',
      billingAddress1: '',
      billingAddress2: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      billingCountry: 'US',
      billingPhone: '',
      ccNumber: '',
      ccExp: '',
      cvv: '',
    },
    onSubmit: async (values) => {
      const isCreating = values.organizationType === 'create';
      const type: 'create' | 'join' = isCreating ? 'create' : 'join';

      try {
        setErrorMessage('');

        const result = await register({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          // Only sent when creating: the backend branches on its presence, and an empty
          // string would be treated as a name.
          organizationName: isCreating ? values.organizationName : undefined,
          planId: isCreating ? values.planId : undefined,
          // Present only for a card-required plan; its presence is what routes the
          // request to the paid endpoint.
          payment:
            isCreating && values.requiresCard
              ? {
                  billingPeriod: values.billingPeriod as 'MONTHLY' | 'ANNUAL',
                  address1: values.billingAddress1,
                  address2: values.billingAddress2 || undefined,
                  city: values.billingCity,
                  state: values.billingState,
                  zip: values.billingZip,
                  country: values.billingCountry || 'US',
                  phone: values.billingPhone || undefined,
                  // NMI wants digits only.
                  ccNumber: values.ccNumber.replace(/\s/g, ''),
                  ccExp: values.ccExp.replace(/\//g, ''),
                  cvv: values.cvv,
                }
              : undefined,
        });

        if (result.status !== 'FAILURE') {
          track('sign_up_completed', {
            type,
            plan: isCreating ? values.selectedPlanName || undefined : undefined,
          });
        } else {
          const reason_code =
            result.statusCode && KNOWN_REASON_CODES.has(result.statusCode)
              ? result.statusCode
              : 'unknown';
          track('sign_up_failed', { type, reason_code });
        }

        if (
          result.status === 'FAILURE' &&
          (result.statusCode === 'ALREADY_INVITED' ||
            result.statusCode === 'EMAIL_ALREADY_REGISTERED')
        ) {
          setInvitedEmail(values.email);
          setExistingAccountReason(
            result.statusCode === 'EMAIL_ALREADY_REGISTERED'
              ? 'registered'
              : 'invited',
          );
          setShowAlreadyInvited(true);
          return;
        }

        if (result.status === 'FAILURE') {
          setErrorMessage(
            result.errorMessage || tLogin.registrationFailed,
          );
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // useRegister owns the redirect on success.
      } catch (error: any) {
        // Defense-in-depth: useRegister never throws today (see docs/architecture.md ## Sprint 21),
        // so this branch is not currently reachable in production, but is instrumented in case
        // that ever changes.
        track('sign_up_failed', { type, reason_code: 'exception' });
        setErrorMessage(
          error?.response?.data?.errorMessage ||
            error?.message ||
            tLogin.unexpectedError,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
  });

  if (isLoading) {
    return <Loading size="2xl" center />;
  }

  if (showAlreadyInvited) {
    return (
      <div className="bg-white dark:bg-gray-900 min-h-screen pb-8">
        <Header />
        <AccountExists
          setShowAlreadyInvited={setShowAlreadyInvited}
          invitedEmail={invitedEmail}
          reason={existingAccountReason}
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen pb-8">
      <Header />
      <div className="pt-4">
        {errorMessage && (
          <div className="max-w-2xl mx-auto px-4 pb-4 mt-20">
            <Alert variant="error" showIcon title={tLogin.registrationError}>
              <p>{errorMessage}</p>
            </Alert>
          </div>
        )}
        <RegisterForm formik={registerFormik} />
      </div>
    </div>
  );
};

export default RegisterPage;
